import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { LayoutDashboard, Radar, Layers, Bell, Star, Settings as SettingsIcon, Activity, SlidersHorizontal, CalendarClock } from "lucide-react";
import { getWatchlist, addWatch, getAlerts, getScanner, getSettings } from "@/lib/api";
import { playSound } from "@/lib/sounds";
import { MarketMarquee } from "@/components/MarketMarquee";
import { ScannerTable } from "@/components/ScannerTable";
import { OptionsPanel } from "@/components/OptionsPanel";
import { AlertsFeed } from "@/components/AlertsFeed";
import { Watchlist } from "@/components/Watchlist";
import { SettingsPanel } from "@/components/SettingsPanel";
import { RulesPanel } from "@/components/RulesPanel";
import { EarningsPanel } from "@/components/EarningsPanel";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "scanner", label: "Scanner", icon: Radar },
  { id: "options", label: "Options", icon: Layers },
  { id: "rules", label: "Rules", icon: SlidersHorizontal },
  { id: "earnings", label: "Earnings", icon: CalendarClock },
  { id: "alerts", label: "Alerts", icon: Bell },
  { id: "watchlist", label: "Watchlist", icon: Star },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

// Short two-note chime for high-confidence signals (WebAudio, no asset needed)
const HIGH_CONF = new Set(["breakout", "breakdown", "custom"]);

// Push new alerts as toast notifications + optional direction-based chime
const useAlertNotifier = (settings) => {
  const seen = useRef(null);
  const { data = [] } = useQuery({ queryKey: ["alerts-notify"], queryFn: () => getAlerts(20), refetchInterval: 20000 });
  useEffect(() => {
    if (!data.length) return;
    const ids = new Set(data.map((a) => a.id));
    if (seen.current === null) { seen.current = ids; return; }
    const fresh = data.filter((a) => !seen.current.has(a.id));
    seen.current = ids;
    fresh.slice(0, 3).forEach((a) => {
      toast(`${a.symbol} · ${a.suggestion}`, { description: a.message });
    });
    const soundOn = settings?.sound_enabled ?? true;
    if (soundOn) {
      const hi = fresh.filter((a) => HIGH_CONF.has(a.kind));
      if (hi.some((a) => a.direction === "bullish")) playSound(settings?.sound_bullish || "rise");
      else if (hi.some((a) => a.direction === "bearish")) playSound(settings?.sound_bearish || "drop");
    }
  }, [data, settings]);
};

function App() {
  const [view, setView] = useState("dashboard");
  const [symbol, setSymbol] = useState(null);
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  useAlertNotifier(settings);

  const { data: watch = [] } = useQuery({ queryKey: ["watchlist"], queryFn: getWatchlist, refetchInterval: 20000 });
  const starred = watch.map((w) => w.symbol);

  const selectTicker = (sym) => { setSymbol(sym); setView("options"); };
  const star = async (sym) => {
    await addWatch(sym);
    qc.invalidateQueries({ queryKey: ["watchlist"] });
    toast.success(`${sym} added to watchlist`);
  };

  const Header = () => (
    <header className="sticky top-0 z-40 bg-app/95 backdrop-blur-md border-b border-border">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded bg-gradient-to-br from-ai to-neutral flex items-center justify-center">
            <Activity size={17} className="text-black" />
          </div>
          <div>
            <div className="font-bold text-white text-sm leading-none tracking-tight">OPTION TRADE MACHINE</div>
            <div className="text-[10px] font-mono text-tertiary flex items-center gap-1 mt-0.5">
              <span className="live-dot h-1.5 w-1.5 rounded-full bg-bull inline-block" /> LIVE · updates every 60s
            </div>
          </div>
        </div>
      </div>
      <MarketMarquee />
    </header>
  );

  const renderView = () => {
    switch (view) {
      case "scanner":
        return <ScannerTable onSelect={selectTicker} onStar={star} starred={starred} />;
      case "options":
        return <OptionsPanel symbol={symbol} />;
      case "rules":
        return <RulesPanel />;
      case "earnings":
        return <EarningsPanel onSelect={selectTicker} />;
      case "alerts":
        return <AlertsFeed onSelect={selectTicker} />;
      case "watchlist":
        return <Watchlist onSelect={selectTicker} />;
      case "settings":
        return <SettingsPanel />;
      default:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-8 space-y-4">
              <ScannerTable onSelect={selectTicker} onStar={star} starred={starred} />
            </div>
            <div className="lg:col-span-4 space-y-4">
              <OptionsPanel symbol={symbol} />
              <AlertsFeed onSelect={selectTicker} compact />
              <Watchlist onSelect={selectTicker} />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="App min-h-screen bg-app text-white">
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#0E0E0E", border: "1px solid #222225", color: "#fff", fontFamily: "IBM Plex Mono, monospace", fontSize: "12px" } }} />
      <Header />

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col w-52 shrink-0 border-r border-border min-h-[calc(100vh-97px)] p-3 gap-1 sticky top-[97px] self-start">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => setView(n.id)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors ${
                view === n.id ? "bg-surface-2 text-white" : "text-secondary hover:bg-surface hover:text-white"
              }`}>
              <n.icon size={17} className={view === n.id ? "text-ai" : ""} />
              {n.label}
            </button>
          ))}
          {symbol && (
            <div className="mt-auto p-3 rounded bg-surface border border-border">
              <div className="text-[10px] font-mono text-tertiary uppercase">Active ticker</div>
              <div className="font-mono font-bold text-white">{symbol}</div>
            </div>
          )}
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 p-3 md:p-5 pb-24 md:pb-8">{renderView()}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-app/95 backdrop-blur-md border-t border-border flex justify-between overflow-x-auto no-scrollbar py-1.5 px-1">
        {NAV.filter((n) => n.id !== "dashboard").map((n) => (
          <button key={n.id} data-testid={`mnav-${n.id}`} onClick={() => setView(n.id)}
            className={`flex flex-col items-center gap-0.5 px-2 py-1.5 min-w-[52px] shrink-0 ${view === n.id ? "text-ai" : "text-tertiary"}`}>
            <n.icon size={19} />
            <span className="text-[9px] font-mono uppercase tracking-wide">{n.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default App;
